import { Link } from "react-router-dom";
import { apiBaseUrl } from "../lib/cmsApi";
import { resolveCmsImage } from "../lib/cmsAssets";
import homeFallbackContent from "../lib/fallbackContent/home";
import { mergeCmsSection } from "../lib/mergeCmsSection";
import collageTop from "../assets/home-industry/partnership-collage-top.png";
import collageBl from "../assets/home-industry/partnership-collage-bl.png";
import collageBr from "../assets/home-industry/partnership-collage-br.png";

const LOCAL_FALLBACKS = {
  industry_collage_top: collageTop,
  industry_collage_bl: collageBl,
  industry_collage_br: collageBr,
};

function resolveSlot(slot = {}, localKey) {
  const key = slot.imageKey || localKey;
  return (
    slot.image ||
    resolveCmsImage(key, apiBaseUrl) ||
    LOCAL_FALLBACKS[key] ||
    LOCAL_FALLBACKS[localKey] ||
    ""
  );
}

/**
 * Industry & Institutional Partnerships — spacing aligned to design.
 */
export default function IndustryPartnerships({ data }) {
  const section = mergeCmsSection(data, homeFallbackContent.industry_partnerships);
  const images = section.images || {};
  const paragraphs = Array.isArray(section.paragraphs) ? section.paragraphs : [];
  const ctaLink = section.ctaLink || "/contact-us";
  const ctaText = section.ctaText || "Partner With ASSET";

  const topSrc = resolveSlot(images.top, "industry_collage_top");
  const blSrc = resolveSlot(images.bottomLeft, "industry_collage_bl");
  const brSrc = resolveSlot(images.bottomRight, "industry_collage_br");

  return (
    <section
      className="relative z-[2] bg-white px-4 py-14 sm:px-8 sm:py-16 lg:px-14 lg:py-20"
      aria-labelledby="industry-partnerships-heading"
    >
      <div className="mx-auto max-w-[1100px]">
        <h2
          id="industry-partnerships-heading"
          className="text-[26px] font-bold leading-tight tracking-tight text-[#183B56] sm:text-[32px] lg:text-[40px]"
        >
          {section.title || "Industry & Institutional Partnerships"}
        </h2>

        <div className="mt-9 grid items-start gap-12 sm:mt-11 lg:mt-12 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-x-[72px] xl:gap-x-[88px]">
          {/* Image collage */}
          <div className="relative pl-4 pt-4 sm:pl-5 sm:pt-5">
            {/* Yellow frame: top + left + partial bottom (under BL) */}
            <div
              className="pointer-events-none absolute left-0 top-0 z-0 h-full w-[72%] rounded-tl-[18px] rounded-bl-[18px] border-b-[3px] border-l-[3px] border-t-[3px] border-[#F5C542] sm:rounded-tl-[22px] sm:rounded-bl-[22px] sm:border-b-[4px] sm:border-l-[4px] sm:border-t-[4px]"
              aria-hidden
            />
            {/* Cap the bottom border so it doesn't run under BR image */}
            <div
              className="pointer-events-none absolute bottom-0 right-0 z-[1] h-[4px] w-[42%] bg-white sm:h-[5px]"
              aria-hidden
            />

            <div className="relative z-[2] grid gap-3 sm:gap-[14px]">
              <img
                src={topSrc}
                alt={images.top?.alt || "Team collaboration"}
                loading="lazy"
                className="aspect-[16/9] w-full rounded-[12px] object-cover sm:rounded-[14px]"
              />
              <div className="grid grid-cols-2 gap-3 sm:gap-[14px]">
                <img
                  src={blSrc}
                  alt={images.bottomLeft?.alt || "Industry meeting"}
                  loading="lazy"
                  className="aspect-[4/5] w-full rounded-[12px] object-cover sm:rounded-[14px]"
                />
                <img
                  src={brSrc}
                  alt={images.bottomRight?.alt || "Mentorship"}
                  loading="lazy"
                  className="aspect-[4/5] w-full rounded-[12px] object-cover sm:rounded-[14px]"
                />
              </div>
            </div>
          </div>

          {/* Copy column — top flush with images */}
          <div className="flex flex-col lg:pt-5">
            <h3 className="text-[22px] font-bold leading-snug text-[#111827] sm:text-[26px] lg:text-[28px]">
              {section.subtitle || "Building the Future Together"}
            </h3>

            <div className="mt-5 space-y-[18px] text-[15px] leading-[1.75] text-[#4B5563] sm:mt-6 sm:space-y-5 sm:text-[16px]">
              {paragraphs.map((paragraph, index) => (
                <p key={`ip-p-${index}`}>{paragraph}</p>
              ))}
            </div>

            <Link
              to={ctaLink}
              className="mt-9 inline-flex w-fit items-center gap-2 rounded-md bg-[#1565D8] px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-[#1256c0] sm:mt-11 sm:rounded-lg sm:px-6 sm:py-3 sm:text-[15px]"
            >
              {ctaText}
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
